/**
 * The shared price axis a footprint is read against.
 *
 * A footprint is not a row of independent columns. Its whole value is that
 * every column is drawn against the same price rows, so the eye can run
 * horizontally along one price and see where the volume moved to. Columns
 * scaled to their own ranges look like a footprint and are a different chart:
 * two adjacent columns would put different prices at the same height, and
 * "acceptance at 4250" — the read the chart exists for — becomes unreadable.
 *
 * So the axis is computed once across the whole window, here, rather than per
 * column in a renderer. It also means web and mobile cannot disagree about
 * where a row sits.
 */
import { Decimal, dec } from '../money.js';
import type { FootprintBar } from './footprint.js';
import { UNREADABLE_ABOVE, unclassifiedShare } from './footprint.js';

export interface FootprintAxis {
  /** Prices from the top of the window down, at the instrument's precision. */
  readonly rows: readonly string[];
  /** Highest and lowest price that traded in the window. */
  readonly high: string;
  readonly low: string;
}

/**
 * Rows spanning every price that traded in the window.
 *
 * Null when the window spans more rows than `maxRows`, and null rather than a
 * clipped axis on purpose. A footprint missing its extremes is not a smaller
 * footprint — the trader cannot see that the top of the range is absent, and
 * the rows they can see look like the whole bar. The caller says the window is
 * too wide instead, which the trader can act on.
 *
 * Also null for a window in which nothing traded, which is the same "there is
 * no axis here" answer and does not need a second shape.
 */
export function footprintAxis(
  bars: readonly FootprintBar[],
  /**
   * The height of one row in price — `rowSize(tickSize, ticksPerRow)`, not the
   * raw tick size. A footprint grouped four ticks to a row has rows four ticks
   * apart, and an axis built on the tick would draw three empty rows between
   * every pair that traded.
   */
  rowHeight: Decimal,
  precision: number,
  maxRows = 400,
): FootprintAxis | null {
  if (rowHeight.lte(0)) return null;

  let high: Decimal | null = null;
  let low: Decimal | null = null;
  for (const bar of bars) {
    for (const level of bar.levels) {
      const p = dec(level.price);
      if (high === null || p.gt(high)) high = p;
      if (low === null || p.lt(low)) low = p;
    }
  }
  if (high === null || low === null) return null;

  // `Decimal` is exact decimal arithmetic, so this quotient is a whole number
  // of rows whenever the axis is on the same grid the bars were bucketed on.
  // When it is not — a caller passing one instrument's tick size with another
  // instrument's bars — every row below the top would land at a price that
  // never traded, and the column would still render. Null is the honest answer.
  const span = high.minus(low).div(rowHeight);
  if (!span.isInteger()) return null;

  const rows = span.toNumber() + 1;
  if (rows > maxRows) return null;

  const out: string[] = [];
  for (let i = 0; i < rows; i += 1) {
    out.push(high.minus(rowHeight.times(i)).toFixed(precision));
  }
  return { rows: out, high: high.toFixed(precision), low: low.toFixed(precision) };
}

/**
 * What a column's delta says, or that it says nothing.
 *
 * Descriptive, never directive (CLAUDE.md non-negotiable 4). `buying` means
 * more volume lifted offers than hit bids in this bar — a statement about what
 * happened, not about what to do.
 *
 * `unreadable` is the load-bearing case. A bar whose volume is mostly
 * unclassified has a delta computed from the minority that could be assigned,
 * and rendering that as a conclusion is worse than rendering nothing: it is a
 * confident number derived from a sample nobody chose.
 */
export type DeltaTone = 'buying' | 'selling' | 'even' | 'unreadable';

export function deltaTone(bar: FootprintBar): DeltaTone {
  if (unclassifiedShare(bar) > UNREADABLE_ABOVE) return 'unreadable';
  if (bar.delta > 0) return 'buying';
  if (bar.delta < 0) return 'selling';
  return 'even';
}

/**
 * The busiest row in a column, for scaling its heat.
 *
 * Per column rather than across the window: a footprint's shading answers
 * "where in THIS bar did the volume sit", and scaling to the window's busiest
 * bar leaves every quiet bar uniformly blank. Returns 0 for an empty column so
 * a caller dividing by it must handle the case rather than silently getting a
 * ratio out of nothing.
 */
export function busiestRow(bar: FootprintBar): number {
  let peak = 0;
  for (const level of bar.levels) if (level.total > peak) peak = level.total;
  return peak;
}
